import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { navLinks } from '../../data/mock';
import { Button } from '../ui/button';

const LangSwitch = () => {
  const { i18n } = useTranslation();
  const base = 'px-2 py-0.5 rounded text-sm transition-colors';
  const active = 'bg-emerald-100 text-emerald-700 font-semibold';
  const idle = 'text-gray-500 hover:text-gray-700';

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => i18n.changeLanguage('pl')}
        className={`${base} ${i18n.language?.startsWith('pl') ? active : idle}`}
      >
        PL
      </button>
      <button
        type="button"
        onClick={() => i18n.changeLanguage('en')}
        className={`${base} ${i18n.language?.startsWith('en') ? active : idle}`}
      >
        EN
      </button>
    </div>
  );
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="text-gray-900 font-semibold text-xl tracking-tight">AuraBase</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="px-4 py-2 text-[15px] text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
              >
                {t(`landing.nav.${link.key}`)}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <LangSwitch />
            {user ? (
              <>
                <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                  {t('landing.header.dashboard')}
                </Button>
                <Button variant="outline" onClick={logout}>
                  {t('landing.header.logout')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')}>
                  {t('landing.header.login')}
                </Button>
                <Button onClick={() => navigate('/register')}>
                  {t('landing.header.get_started')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-600 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="block text-[16px] text-gray-600 hover:text-gray-900 py-2"
              >
                {t(`landing.nav.${link.key}`)}
              </a>
            ))}
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <LangSwitch />
              {user ? (
                <>
                  <Button className="w-full" onClick={() => navigate('/dashboard')}>
                    {t('landing.header.dashboard')}
                  </Button>
                  <Button variant="outline" className="w-full" onClick={logout}>
                    {t('landing.header.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                    {t('landing.header.login')}
                  </Button>
                  <Button className="w-full" onClick={() => navigate('/register')}>
                    {t('landing.header.get_started')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
